// frontend/src/pages/dashboard/TaskComponents/TaskDetailsDialog.js

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  Chip,
  Avatar,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { 
  Close as CloseIcon,       
  Download as DownloadIcon  
} from "@mui/icons-material";
import { convertToCompanyTime } from "../../../utils/taskHelpers";
import { getAvatarUrl } from "../../../utils/imageHelpers";

const TaskDetailsDialog = ({
  open,
  onClose,
  viewingTask,
  attachments,
  handleDownload,
  company,
}) => {
  if (!viewingTask) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Task Details: {viewingTask.title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              <Typography variant="body2" color="text.secondary">
                {viewingTask.description || "No description provided"}
              </Typography>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Project
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              <Typography variant="body2">
                {viewingTask.project?.name || "N/A"}
              </Typography>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              <Chip
                label={(viewingTask.statusObj?.name || "Unknown").toUpperCase()}
                sx={{
                  backgroundColor: viewingTask.statusObj?.color || "#9CA3AF",
                  color: "#fff",
                }}
                size="small"
              />
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Priority
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              <Chip
                label={
                  viewingTask.priority.charAt(0).toUpperCase() +
                  viewingTask.priority.slice(1)
                }
                color={
                  viewingTask.priority === "high" ||
                  viewingTask.priority === "urgent"
                    ? "error"
                    : viewingTask.priority === "low"
                    ? "success"
                    : "warning"
                }
                size="small"
              />
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              Due Date
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              <Typography variant="body2">
                {viewingTask.due_date
                  ? convertToCompanyTime(viewingTask.due_date, company.timezone)
                  : "N/A"}
              </Typography>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assigned Users
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              {viewingTask.assignedUsers?.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {viewingTask.assignedUsers.map((user) => (
                    <Tooltip key={user.id} title={user.name}>
                      <Avatar
                        sx={{ width: 32, height: 32 }}
                        src={getAvatarUrl(user.avatar)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = null;
                        }}
                      >
                        {!user.avatar ? user.name.charAt(0) : null}
                      </Avatar>
                    </Tooltip>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No users assigned.
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Attachments
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}
            >
              {attachments.length > 0 ? (
                <List dense>
                  {attachments.map((att) => (
                    <ListItem
                      key={att.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleDownload(att)}
                          color="primary"
                        >
                          <DownloadIcon />
                        </IconButton>
                      }
                      sx={{ pl: 0 }}
                    >
                      <ListItemText
                        primary={att.original_name}
                        secondary={`${(att.size / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No attachments.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailsDialog;